/// <amd-module name="Widgets/Configs/TeamWidgetConfig/TeamWidgetConfig" />
import { isNameValid, NameField } from "Dashboards/CommonConfig/NameField";
import { SizePicker } from "Dashboards/CommonConfig/SizePicker";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { ConfigProps, LoadingStatus } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import * as React from "react";
import { WebApiTeam } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { TeamWidgetSettings } from "WidgetCommon/ConfigContracts/TeamWidgetSettings";
import { GenericSelectionDropdown } from "Widgets/Configs/Common/GenericSelectionDropdown";
import { convertToListBoxItems, OpStoreTeamsQuery } from "Widgets/Configs/Common/OpStoreTeamsQuery";
import * as Resources from "Widgets/Configs/TeamWidgetConfig/Resources";

interface TeamWidgetConfigState {
    teamOptions: IListBoxItem<WebApiTeam>[] | undefined;
}

/** Common config for producing a team selection. */
export class TeamWidgetConfig extends VssComponent<ConfigProps, TeamWidgetConfigState> {
    public static readonly componentType: string = "TeamWidgetConfig";

    private static parseSettings(settings: string | null): TeamWidgetSettings | null {
        return settings && JSON.parse(settings);
    }

    private static isConfigValid(config: DashboardWidgetProps, supportedSizes: WidgetGridSize[], teamOptions: IListBoxItem<WebApiTeam>[]): boolean {
        const widgetSettings = TeamWidgetConfig.parseSettings(config.settings);
        return (
            isNameValid(config.name) &&
            supportedSizes.some(
                supportedSize => supportedSize.columnSpan === config.size.columnSpan && supportedSize.rowSpan === config.size.rowSpan
            ) &&
            (widgetSettings === null || teamOptions.some(teamOption => teamOption.id === widgetSettings.teamId))
        );
    }

    public constructor(props: ConfigProps) {
        super(props);
        this.state = { teamOptions: undefined };
    }

    public async componentDidMount(): Promise<void> {
        try {
            const projectId = this.getProjectId();
            const teamOptions = await this.trackPromise(this.getTeamListItems(this.context.pageContext, projectId)).promise;

            // Use default team for starting settings
            if (this.props.config.settings == null) {
                const defaultTeamOption = teamOptions.find(teamOption => teamOption.id === this.props.teamContext.id);
                if (defaultTeamOption) {
                    this.onTeamSelect(defaultTeamOption);
                }
            }

            this.setState({ teamOptions });
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were canceled
            }

            this.props.parentProps.onConfigLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: e
            });
        }
    }

    public componentDidUpdate(prevProps: ConfigProps, prevState: TeamWidgetConfigState): void {
        if (prevState.teamOptions == null && this.state.teamOptions != null) {
            this.props.parentProps.onConfigLoaded();
        }

        if (prevProps.saveValidationTimeStamp != this.props.saveValidationTimeStamp) {
            const currentConfig = this.props.config;
            this.props.parentProps.onValidationResponse(
                currentConfig,
                TeamWidgetConfig.isConfigValid(currentConfig, this.props.contributionConstraints.supportedSizes, this.state.teamOptions!)
            );
        }
    }

    public render(): JSX.Element | null {
        if (this.state.teamOptions != null) {
            const widgetSettings = TeamWidgetConfig.parseSettings(this.props.config.settings);
            return (
                <div className="team-widget-config">
                    {this.props.contributionConstraints.isNameConfigurable && (
                        <NameField value={this.props.config.name} onChange={this.onNameChanged} />
                    )}
                    {this.props.contributionConstraints.supportedSizes.length > 1 && (
                        <SizePicker
                            onChange={this.onSizeChanged}
                            sizes={this.props.contributionConstraints.supportedSizes}
                            value={this.props.config.size}
                        />
                    )}
                    <FormItem label={Resources.TeamLabel} className="team-widget-config-form-item">
                        <GenericSelectionDropdown
                            className="flex-grow"
                            items={this.state.teamOptions}
                            selectedId={widgetSettings ? widgetSettings.teamId : undefined}
                            onSelect={this.onTeamSelect}
                        />
                    </FormItem>
                </div>
            );
        }

        return null;
    }

    private async getTeamListItems(pageContext: IVssPageContext, projectId: string): Promise<IListBoxItem<WebApiTeam>[]> {
        const queryCache = pageContext.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        const teams = await queryCache.getCacheableQueryResult(OpStoreTeamsQuery.onProject(projectId));
        return convertToListBoxItems(teams);
    }

    private onNameChanged = (name: string): void => {
        this.props.parentProps.onUpdate({
            ...this.props.config,
            name
        });
    };

    private onSizeChanged = (size: WidgetGridSize): void => {
        this.props.parentProps.onUpdate({
            ...this.props.config,
            size
        });
    };

    private onTeamSelect = (teamItem: IListBoxItem<WebApiTeam>): void => {
        const newSettings: TeamWidgetSettings = {
            teamId: teamItem.id
        };

        this.props.parentProps.onUpdate({
            ...this.props.config,
            settings: JSON.stringify(newSettings)
        });
    };

    private getProjectId(): string {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (!tfsPageData || !tfsPageData.project) {
            throw new Error("Couldn't fetch project information.");
        }
        return tfsPageData.project.id;
    }
}

VssComponent.register(TeamWidgetConfig.componentType, TeamWidgetConfig);
